import { BaseQueryParams, DacDetails, IResponsePaginate, Tasks, WithMetadata } from '@/types';
import { AdditiveDetails, Meeting, MeetingDto, MeetingHistory, ParticipantDto, Snippet, UploadSnippetDto } from '@/types/meeting.types';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class MeetingService {
  httpClient = inject(HttpClient);
  BASEURL = `${environment.api.paramsUrl}/reunions`;
  PARAMSBASEURL = `${environment.api.paramsUrl}`;
  PPMBASEURL = `${environment.api.ppmUrl}/ppms`;
  DACBASEURL = `${environment.api.dacUrl}`;
  ADDITIVESURL = `${environment.api.eclaircissementUrl}`;
  REPORTURL = `${environment.api.reportUrl}`;

  MEETING_HISTORY_BASEURL = `${this.BASEURL}/history`;
  environment = environment.api.authUrl;

  getAll(params?: BaseQueryParams): Observable<IResponsePaginate<Meeting>> {
    return this.httpClient.get<IResponsePaginate<Meeting>>(this.BASEURL, { params: params as HttpParams });
  }

  getOne(id: number): Observable<WithMetadata<Meeting>> {
    return this.httpClient.get<WithMetadata<Meeting>>(`${this.BASEURL}/${id}`);
  }

  cancel(id: number): Observable<WithMetadata<Meeting>> {
    return this.httpClient.put<WithMetadata<Meeting>>(`${this.BASEURL}/${id}/annuler`, {});
  }

  create(meeting: MeetingDto): Observable<WithMetadata<Meeting>> {
    return this.httpClient.post<WithMetadata<Meeting>>(`${this.BASEURL}`, meeting);
  }

  saveAsDraft(id: number, meeting: MeetingDto): Observable<WithMetadata<Meeting>> {
    return this.httpClient.put<WithMetadata<Meeting>>(`${this.BASEURL}/${id}/brouillon`, meeting);
  }

  validateDraft(id: number, meeting: MeetingDto): Observable<WithMetadata<Meeting>> {
    return this.httpClient.put<WithMetadata<Meeting>>(`${this.BASEURL}/${id}/planifier_or_not`, meeting);
  }

  delete(id: number): Observable<WithMetadata<Meeting>> {
    return this.httpClient.delete<WithMetadata<Meeting>>(`${this.BASEURL}/${id}`);
  }

  getComTasksPpms(comId: number): Observable<Tasks> {
    return this.httpClient.get<Tasks>(`${this.PPMBASEURL}/flowable/tasks/by_com/${comId}`);
  }

  getComTasksDacs(comId: number): Observable<Tasks> {
    return this.httpClient.get<Tasks>(`${this.DACBASEURL}/flowable/tasks/by_com/${comId}`);
  }

  getComTasksAdditives(comId: number): Observable<Tasks> {
    return this.httpClient.get<Tasks>(`${this.ADDITIVESURL}/flowable/tasks/by_com/${comId}`);
  }

  getComTasksReports(comId: number): Observable<Tasks> {
    return this.httpClient.get<Tasks>(`${this.REPORTURL}/flowable/report/tasks/by_com/${comId}`);
  }
  getSelectedUserAcs(userId: number): Observable<{ id: number; nom: string }[]> {
    return this.httpClient.get<{ id: number; nom: string }[]>(`${this.PARAMSBASEURL}/ac/acs_by_userId?userId=${userId}`);
  }

  syncParticipants(id: number): Observable<any> {
    return this.httpClient.put(`${this.PARAMSBASEURL}/participant/${id}/synchronize-participants`, {});
  }

  startMeeting(id: number): Observable<any> {
    return this.httpClient.put<any>(`${this.BASEURL}/${id}/start`, {});
  }
  addParticipant(meetingId: number, invitee: ParticipantDto): Observable<WithMetadata<Meeting>> {
    return this.httpClient.post<WithMetadata<Meeting>>(`${this.PARAMSBASEURL}/participant/${meetingId}/add_new_invited_participant`, {
      ...invitee,
    });
  }

  updateConvenedMeeting(meetingId: number, formData: FormData): Observable<WithMetadata<Meeting>> {
    return this.httpClient.put<WithMetadata<Meeting>>(`${this.BASEURL}/${meetingId}/update-after-start`, formData);
  }

  downloadPvDraft(meetingId: number): Observable<Blob> {
    return this.httpClient.get(`${this.BASEURL}/pv-draft/${meetingId}`, { responseType: 'blob' });
  }

  downloadPvWithMeeting(meetingId: number): Observable<Blob> {
    return this.httpClient.get(`${this.BASEURL}/pv/${meetingId}`, { responseType: 'blob' });
  }

  getDacTasks(taskId: string): Observable<DacDetails> {
    return this.httpClient.get<DacDetails>(`${this.DACBASEURL}/flowable/tasks/${taskId}`);
  }

  getRequestsTasks(taskId: string): Observable<AdditiveDetails> {
    return this.httpClient.get<AdditiveDetails>(`${this.ADDITIVESURL}/flowable/tasks/${taskId}`);
  }

  getReportTasks(taskId: string): Observable<AdditiveDetails> {
    return this.httpClient.get<AdditiveDetails>(`${this.REPORTURL}/flowable/report/tasks/${taskId}`);
  }

  validate(meetingId: number, formData: FormData): Observable<WithMetadata<Meeting>> {
    return this.httpClient.put<WithMetadata<Meeting>>(`${this.BASEURL}/${meetingId}/validate`, formData);
  }

  validateGroupPpm(meetingId: number, dto: FormData): Observable<any> {
    return this.httpClient.post(`${this.PPMBASEURL}/flowable/tasks/validate-group/${meetingId}`, dto);
  }

  validateGroupDac(meetingId: number, dto: FormData): Observable<any> {
    return this.httpClient.post(`${this.DACBASEURL}/flowable/tasks/validate-group/${meetingId}`, dto);
  }

  validateGroupQuestions(meetingId: number, dto: FormData): Observable<any> {
    return this.httpClient.post(`${this.ADDITIVESURL}/flowable/tasks/validate-group/${meetingId}`, dto);
  }
  validateGroupReports(meetingId: number, dto: FormData): Observable<any> {
    return this.httpClient.post(`${this.REPORTURL}/flowable/report/tasks/validate-group/${meetingId}`, dto);
  }
  getSnippetsByMeetingId(meetingId: number): Observable<IResponsePaginate<Snippet>> {
    return this.httpClient.get<IResponsePaginate<Snippet>>(`${this.BASEURL}/${meetingId}/extraits`);
  }

  uploadSignedPv(meetingId: number, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.put<any>(`${this.BASEURL}/${meetingId}/upload-pv`, formData);
  }

  uploadSnippet(meetingId: number, dto: UploadSnippetDto): Observable<any> {
    const formData = new FormData();
    formData.append('extraitId', dto.extraitId.toString());
    formData.append('acId', dto.acId.toString());
    formData.append('file', dto.file);
    return this.httpClient.put<any>(`${this.BASEURL}/${meetingId}/extraits`, formData);
  }

  downloadSnippet(meetingId: number, acId: number): Observable<Blob> {
    return this.httpClient.get(`${this.BASEURL}/extrait-pv-cpmp/${meetingId}/${acId}/download`, { responseType: 'blob' });
  }

  downloadSnippetCncmp(data: any): Observable<Blob> {
    return this.httpClient.post(`${this.BASEURL}/extrait-pv-cncmp/download`, data, { responseType: 'blob' });
  }

  getDossiers(type: 'ppm' | 'dac'): Observable<{ id: number; ref: string }[]> {
    const url = type === 'ppm' ? this.PPMBASEURL : `${this.DACBASEURL}/dac`;
    return this.httpClient.get<{ id: number; ref: string }[]>(`${url}/drop-list`);
  }

  getMeetingHistoryDetails(meetingId: string, dossierId: string, type: 'ppm' | 'dac'): Observable<WithMetadata<MeetingHistory>> {
    return this.httpClient.get<WithMetadata<MeetingHistory>>(`${this.MEETING_HISTORY_BASEURL}/${meetingId}/${type}/${dossierId}`);
  }

  getMeetingHistory(type: 'ppm' | 'dac', dossierId: number, comId?: number, params?: BaseQueryParams): Observable<IResponsePaginate<Meeting>> {
    let httpParams: HttpParams = new HttpParams();
    if (comId) httpParams = httpParams.append('comId', comId);
    if (params) httpParams = httpParams.appendAll(params as any);
    return this.httpClient.get<IResponsePaginate<Meeting>>(`${this.MEETING_HISTORY_BASEURL}/pageable/${type}/${dossierId}`, {
      params: httpParams,
    });
  }

  getCommitteesByDossierId(dossierId: number, type: 'ppm' | 'dac'): Observable<any> {
    return this.httpClient.get<any>(`${this.MEETING_HISTORY_BASEURL}/drop-list-com/${type}/${dossierId}`);
  }
}
